import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle2,
  Loader2,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDuration } from "@/lib/formatters";
import { getRunDetail } from "@/api/engine";
import { getRunStatusColor } from "@/lib/constants";
import { useAuthStore } from "@/stores/auth.store";
import type { JobRun, JobRunDetail as JobRunDetailType } from "@/api/types";
import { getPappersStatsView } from "@/lib/pappersStats";
import { PappersRunStats } from "./PappersRunStats";
import { EmailSyncRunStats } from "./EmailSyncRunStats";
import { isEmailSyncJob } from "@/lib/email";

/** First poll fires fast so the sheet picks up activity right after
 *  the trigger response arrives. The next ~5 polls run at FAST_POLL_MS
 *  to catch progressive log bytes early in a run, then we settle into
 *  IDLE_POLL_MS to keep the request rate down for long-running jobs. */
const INITIAL_POLL_DELAY_MS = 300;
const FAST_POLL_MS = 500;
const FAST_POLL_COUNT = 5;
const IDLE_POLL_MS = 1500;
/** Historical-mode tail cadence — used when a run viewed from the
 *  Runs page is still in flight. Slower than the live triggered-now
 *  cadence since the operator opens these less reactively. */
const HISTORICAL_TAIL_POLL_MS = 2000;
/** How long to wait between newly-revealed log lines when staggering
 *  the first batch of an already-finished run. */
const STAGGER_DELAY_MS = 80;
/** Threshold for triggering the stagger reveal. Below this, the first
 *  batch is appended in one go — there's not enough to fake a "live"
 *  console. */
const STAGGER_THRESHOLD = 3;
const IN_FLIGHT_STATUSES = new Set(["running", "pending", "started"]);
const TERMINAL_STATUSES = new Set([
  "success",
  "succeeded",
  "partial",
  "failed",
  "skipped",
  "cancelled",
  "timed_out",
]);
const SUCCESS_STATUSES = new Set(["success", "succeeded"]);
const SCROLL_THRESHOLD_PX = 50;

interface JobRunLiveSheetProps {
  open: boolean;
  onClose: () => void;
  projectSlug: string;
  jobId: string;
  jobName: string;
  /** Run id resolution per mode:
   *  - live:       opens with `null` while POST /trigger is in flight
   *                (parent fires the POST after opening the sheet) and
   *                is patched to a number once the trigger responds.
   *                The sheet shows a "Triggering…" placeholder during
   *                the null window, then re-keys its polling effect
   *                when the id lands.
   *  - historical: always a number — the run the user clicked. */
  runId: number | null;
  /** "live"       — polls getRunDetail every 1.5 s, appending only the
   *                 new tail of log_output to a growing logLines state.
   *                 Stops polling when the run reaches a terminal status.
   *  "historical" — single fetch on open; renders all lines at once with
   *                 no fade-in, no polling. */
  mode: "live" | "historical";
  /** Live mode only — fired once when the polled run reaches a terminal
   *  status so the parent can refresh its jobs list / toast. */
  onComplete?: (run: JobRunDetailType) => void;
  /** Live mode only — re-trigger the same job (failed runs only). */
  onRetry?: () => void;
}

export function JobRunLiveSheet({
  open,
  onClose,
  projectSlug,
  jobId,
  jobName,
  runId,
  mode,
  onComplete,
  onRetry,
}: JobRunLiveSheetProps) {
  const currentUser = useAuthStore((s) => s.user);

  const [run, setRun] = useState<JobRunDetailType | null>(null);
  const [logLines, setLogLines] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [openedAt, setOpenedAt] = useState<number>(() => Date.now());
  const [now, setNow] = useState<number>(() => Date.now());
  const [completed, setCompleted] = useState(false);

  // Accumulator for the substring already shown — used to compute the
  // diff against the next poll so we only ever append, never re-render
  // the whole log.
  const seenLogRef = useRef<string>("");
  // Tracks whether the user is at the bottom of the log container; used
  // to decide whether to follow new lines or stay where they are.
  const wasAtBottomRef = useRef<boolean>(true);
  const onCompleteFiredRef = useRef<boolean>(false);
  // Stagger machinery — when a fast job has already finished by the
  // time we first poll, dropping every line at once kills the "live
  // console" feel. We instead push lines onto pendingLinesRef and
  // drain one every STAGGER_DELAY_MS, deferring the terminal-state
  // UI swap (StatsCard + Success badge) until the queue empties.
  const pendingLinesRef = useRef<string[]>([]);
  const drainTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstBatchSeenRef = useRef<boolean>(false);
  const pendingTerminalRef = useRef<JobRunDetailType | null>(null);

  // ─── Reset on open / jobId change ─────────────────────────────────
  // We deliberately don't include `runId` here: in live mode the parent
  // opens the sheet with `runId: null` and patches it in once POST
  // /trigger resolves. Resetting on every runId change would wipe the
  // "Triggering…" placeholder + any preview state when the id arrives.
  useEffect(() => {
    if (!open) return;
    setOpenedAt(Date.now());
    setNow(Date.now());
    setRun(null);
    setLogLines([]);
    setErrorMessage(null);
    setCompleted(false);
    seenLogRef.current = "";
    wasAtBottomRef.current = true;
    onCompleteFiredRef.current = false;
    pendingLinesRef.current = [];
    if (drainTimerRef.current) {
      clearTimeout(drainTimerRef.current);
      drainTimerRef.current = null;
    }
    firstBatchSeenRef.current = false;
    pendingTerminalRef.current = null;
  }, [open, jobId]);

  // ─── Live timer (1 s tick while in flight) ────────────────────────
  useEffect(() => {
    if (!open) return;
    if (completed) return;
    const handle = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(handle);
  }, [open, completed]);

  // ─── Fetcher ──────────────────────────────────────────────────────
  // Live mode: recursive setTimeout, fast at first then idle.
  //   t = 300ms              → first poll (catches running runs early)
  //   then 500ms × 5         → keep up with progressive log bytes
  //   then 1500ms steady     → standard cadence for long-running jobs
  // Stops as soon as the run reports a terminal status. If the very
  // first response is already terminal with > 3 lines, the lines are
  // queued and drained at 80ms each so the user still sees a console
  // scroll instead of an instantaneous dump.
  // Historical mode: one-shot fetch, no polling, no stagger.
  useEffect(() => {
    if (!open || runId == null) return;
    let cancelled = false;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    let pollCount = 0;

    function finishRun(detail: JobRunDetailType) {
      setCompleted(true);
      if (onComplete && !onCompleteFiredRef.current) {
        onCompleteFiredRef.current = true;
        onComplete(detail);
      }
    }

    function startDrain() {
      if (drainTimerRef.current) return;
      const drainOne = () => {
        if (cancelled) {
          drainTimerRef.current = null;
          return;
        }
        if (pendingLinesRef.current.length === 0) {
          drainTimerRef.current = null;
          // Queue empty — fire any deferred terminal-state work so the
          // Success/Failed badge + StatsCard appear *after* the logs
          // finish scrolling.
          if (pendingTerminalRef.current) {
            const detail = pendingTerminalRef.current;
            pendingTerminalRef.current = null;
            finishRun(detail);
          }
          return;
        }
        const next = pendingLinesRef.current.shift()!;
        setLogLines((prev) => [...prev, next]);
        drainTimerRef.current = setTimeout(drainOne, STAGGER_DELAY_MS);
      };
      drainTimerRef.current = setTimeout(drainOne, STAGGER_DELAY_MS);
    }

    function appendLogDiff(detail: JobRunDetailType) {
      const log = detail.log_output ?? "";
      if (log.length <= seenLogRef.current.length) return;
      const newPart = log.slice(seenLogRef.current.length);
      seenLogRef.current = log;
      const lines = newPart.split("\n");
      if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
      if (lines.length === 0) return;

      const isFirstBatch = !firstBatchSeenRef.current;
      firstBatchSeenRef.current = true;
      // Stagger only the first batch — and only if it's substantial.
      // Subsequent polls during a long-running job still append in
      // real time. Lines arriving while a drain is already in flight
      // pile onto the existing queue so order is preserved.
      const shouldStagger =
        (isFirstBatch && lines.length > STAGGER_THRESHOLD) ||
        drainTimerRef.current !== null;
      if (shouldStagger) {
        pendingLinesRef.current.push(...lines);
        if (!drainTimerRef.current) startDrain();
        return;
      }
      setLogLines((prev) => [...prev, ...lines]);
    }

    function schedulePoll() {
      if (cancelled) return;
      pollCount++;
      const delay =
        mode === "historical"
          ? HISTORICAL_TAIL_POLL_MS
          : pollCount < FAST_POLL_COUNT
            ? FAST_POLL_MS
            : IDLE_POLL_MS;
      timeoutHandle = setTimeout(() => void tick(), delay);
    }

    async function tick() {
      try {
        const detail = await getRunDetail(projectSlug, jobId, runId!);
        if (cancelled) return;
        setRun(detail);
        if (mode === "historical") {
          const log = detail.log_output ?? "";
          seenLogRef.current = log;
          const lines = log.split("\n");
          if (lines.length > 0 && lines[lines.length - 1] === "") {
            lines.pop();
          }
          setLogLines(lines);
          // In-flight runs viewed from the Runs page: keep tailing
          // until the status flips terminal so the operator sees the
          // log fill in real time. Terminal runs render once and stop.
          if (TERMINAL_STATUSES.has(detail.status)) {
            setCompleted(true);
            return;
          }
          schedulePoll();
          return;
        }
        appendLogDiff(detail);
        if (TERMINAL_STATUSES.has(detail.status)) {
          // Defer the badge swap if we're still staggering — let the
          // user see the logs finish scrolling first. Otherwise fire
          // immediately.
          if (
            drainTimerRef.current !== null ||
            pendingLinesRef.current.length > 0
          ) {
            pendingTerminalRef.current = detail;
          } else {
            finishRun(detail);
          }
          return; // stop polling
        }
        schedulePoll();
      } catch (err) {
        if (cancelled) return;
        if (seenLogRef.current.length === 0) {
          setErrorMessage(extractMessage(err));
        }
        schedulePoll();
      }
    }

    if (mode === "live") {
      timeoutHandle = setTimeout(() => void tick(), INITIAL_POLL_DELAY_MS);
    } else {
      void tick();
    }
    return () => {
      cancelled = true;
      if (timeoutHandle) clearTimeout(timeoutHandle);
      if (drainTimerRef.current) {
        clearTimeout(drainTimerRef.current);
        drainTimerRef.current = null;
      }
    };
  }, [open, mode, projectSlug, jobId, runId, onComplete]);

  // ─── Auto-scroll to bottom when new lines arrive (only if user was
  // already at the bottom — preserves their reading position
  // otherwise). ────────────────────────────────────────────────────
  const logContainerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!wasAtBottomRef.current) return;
    const el = logContainerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
  }, [logLines.length]);

  function handleScroll() {
    const el = logContainerRef.current;
    if (!el) return;
    wasAtBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD_PX;
  }

  // ─── Derived rendering bits ───────────────────────────────────────
  // While the parent is still POSTing /trigger we have no run yet —
  // surface that as "starting" so the badge reads "Triggering" instead
  // of pretending the run is already on the box.
  const isStarting = mode === "live" && runId == null && !run;
  const status = isStarting ? "starting" : run?.status ?? "running";
  const isRunning = IN_FLIGHT_STATUSES.has(status) || isStarting;
  const isFailed = status === "failed";
  const isSuccess = SUCCESS_STATUSES.has(status);

  const elapsedSec = useMemo(() => {
    if (run?.started_at) {
      const startMs = Date.parse(run.started_at);
      const endMs = run.finished_at ? Date.parse(run.finished_at) : now;
      const ms = (Number.isFinite(endMs) ? endMs : now) - startMs;
      return Math.max(0, Math.floor(ms / 1000));
    }
    return Math.max(0, Math.floor((now - openedAt) / 1000));
  }, [run?.started_at, run?.finished_at, now, openedAt]);

  const triggeredByLabel = useMemo(() => {
    if (!run) return null;
    const tb = run.triggered_by;
    if (tb === "manual") {
      if (currentUser && run.triggered_by_user_id === currentUser.id) {
        return `Manually by ${currentUser.full_name}`;
      }
      return "Manually";
    }
    if (tb === "cron" || tb === "scheduled") return "Scheduled";
    return tb ? humanize(tb) : null;
  }, [run, currentUser]);

  const pappersStats = run ? getPappersStatsView(run) : null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className={cn("flex w-full flex-col gap-0 sm:max-w-[700px]", pappersStats && "overflow-y-auto")}>
        <SheetHeader className="space-y-2">
          {/* pr-10 reserves room for the SheetContent's built-in close X
           *  (absolute right-4 top-4) so the elapsed timer doesn't collide
           *  with it. */}
          <div className="flex items-start justify-between gap-3 pr-10">
            <SheetTitle className="font-mono text-[16px] font-semibold">
              {jobName}
            </SheetTitle>
            <span className="shrink-0 font-mono text-xs text-slate-500 tabular-nums">
              Elapsed: {formatElapsed(elapsedSec)}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={status} pulse={isRunning} />
            {run && (
              <span className="text-[11px] text-slate-400">
                Run #{run.id}
              </span>
            )}
            {triggeredByLabel && (
              <span className="text-[12px] text-slate-500">
                · {triggeredByLabel}
              </span>
            )}
          </div>
          <SheetDescription className="sr-only">
            Live progressive log console with status and stats.
          </SheetDescription>
        </SheetHeader>

        {/* Console — full-bleed dark surface, fills the remaining height */}
        <div
          ref={logContainerRef}
          onScroll={handleScroll}
          className={cn(
            "-mx-6 mt-4 flex-1 overflow-y-auto overflow-x-hidden rounded-xl bg-[#0a0a10] px-6 py-3 font-mono text-[13px] leading-[1.8]",
            // Custom thin always-visible scrollbar so the dark surface
            // doesn't fight the OS chrome.
            "[&::-webkit-scrollbar]:w-1.5",
            "[&::-webkit-scrollbar-track]:bg-transparent",
            "[&::-webkit-scrollbar-thumb]:rounded-full",
            "[&::-webkit-scrollbar-thumb]:bg-slate-700/70",
          )}
          style={{
            minHeight: 500,
            maxHeight: "calc(100vh - 240px)",
          }}
        >
          {errorMessage && logLines.length === 0 ? (
            <div className="text-rose-400">{errorMessage}</div>
          ) : logLines.length === 0 ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" />
              {mode === "live"
                ? isStarting
                  ? "Triggering job…"
                  : "Starting…"
                : "Loading log output…"}
            </div>
          ) : (
            logLines.map((line, i) => (
              <LogLine key={i} index={i} line={line} animate={mode === "live"} />
            ))
          )}
        </div>

        {/* Stats — slide-up + fade-in once the run is terminal */}
        {completed && run && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className={cn("mt-4", pappersStats && "shrink-0")}
          >
            {pappersStats ? (
              <PappersRunStats stats={pappersStats} durationSeconds={run.duration_seconds} />
            ) : isEmailSyncJob(run.job_id) ? (
              <EmailSyncRunStats stats={run.stats} durationSeconds={run.duration_seconds} />
            ) : (
              <StatsCard run={run} />
            )}
          </motion.div>
        )}

        <SheetFooter className="mt-4 border-t border-slate-900/[0.06] pt-4 sm:justify-between sm:gap-2">
          {!completed ? (
            <span className="inline-flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" />
              Running…
            </span>
          ) : isSuccess ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              <CheckCircle2 className="h-3 w-3" />
              Success
            </span>
          ) : isFailed ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700">
              <XCircle className="h-3 w-3" />
              Failed
            </span>
          ) : status === "partial" ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700">
              <XCircle className="h-3 w-3" />
              Completed with errors
            </span>
          ) : (
            <span className="text-xs capitalize text-slate-500">{status}</span>
          )}
          <div className="flex items-center gap-2">
            {completed && isFailed && mode === "live" && onRetry && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => {
                  onRetry();
                  onClose();
                }}
              >
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                Retry
              </Button>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ─── Internals ──────────────────────────────────────────────────────

function LogLine({
  index,
  line,
  animate,
}: {
  index: number;
  line: string;
  animate: boolean;
}) {
  const colorClass = getLineColor(line);
  // Pull a leading `[HH:MM:SS]` timestamp out so we can dim it relative
  // to the message, the way a developer console would.
  const tsMatch = line.match(/^(\[\d{2}:\d{2}:\d{2}\])\s+(.*)$/);
  const body = tsMatch ? (
    <>
      <span className="mr-3 select-none text-slate-600">{tsMatch[1]}</span>
      <span>{tsMatch[2]}</span>
    </>
  ) : (
    <span>{line || " "}</span>
  );
  return (
    <motion.div
      initial={animate ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
      className={cn(
        "rounded px-2 py-1.5 whitespace-pre-wrap break-words",
        colorClass,
      )}
      data-line-index={index}
    >
      {body}
    </motion.div>
  );
}

function StatusBadge({ status, pulse }: { status: string; pulse: boolean }) {
  const colors = getRunStatusColor(status);
  const label = SUCCESS_STATUSES.has(status)
    ? "Success ✓"
    : status === "failed"
      ? "Failed ✗"
      : status === "partial"
        ? "Partial ⚠"
      : status === "running"
        ? "Running"
        : status === "skipped"
          ? "Skipped"
          : status === "starting"
            ? "Triggering"
            : status;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium",
        colors.bg,
        colors.text,
      )}
    >
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          colors.dot,
          pulse && "animate-pulse",
        )}
      />
      {label}
    </span>
  );
}

function StatsCard({ run }: { run: JobRun }) {
  const entries = Object.entries(run.stats ?? {});
  // Render Duration alongside the backend stats — the user spec asks
  // for it inline with the rest of the grid.
  const allEntries: Array<[string, unknown]> =
    run.duration_seconds != null
      ? [...entries, ["duration", formatDuration(run.duration_seconds)]]
      : entries;
  return (
    <div className="rounded-xl bg-white p-4 shadow-card-rest">
      {allEntries.length === 0 ? (
        <p className="text-sm italic text-slate-400">No stats reported</p>
      ) : (
        <div className="grid grid-cols-3 gap-x-4 gap-y-3">
          {allEntries.map(([key, value]) => (
            <Stat key={key} label={key} value={value} />
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: unknown }) {
  const numeric = typeof value === "number" ? value : null;
  const tone =
    numeric == null
      ? "text-slate-900"
      : numeric > 0 && /(error|fail|removed|bounced)/i.test(label)
        ? "text-rose-600"
        : numeric > 0
          ? "text-emerald-600"
          : "text-slate-900";
  return (
    <div className="space-y-0.5">
      <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {humanize(label)}
      </div>
      <div className={cn("text-[20px] font-semibold tabular-nums", tone)}>
        {formatStat(value)}
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function getLineColor(line: string): string {
  if (/\bERROR\b|✗|exception|traceback/i.test(line)) return "text-rose-400";
  if (/✓|\bcompleted\b|sync complete/i.test(line)) return "text-emerald-400";
  if (/\bWARNING\b|⚠/i.test(line)) return "text-amber-400";
  if (/\b(found|fetching|starting|loading)\b/i.test(line)) {
    return "text-blue-400/80";
  }
  // Indented detail lines (start with "[…]" followed by 2+ spaces).
  if (/^\[[^\]]*\]\s{2,}/.test(line)) return "text-slate-500";
  return "text-slate-400";
}

function formatElapsed(secondsTotal: number): string {
  const total = Math.max(0, Math.floor(secondsTotal));
  const hours = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function humanize(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatStat(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "number") return value.toLocaleString("en-US");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function extractMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Couldn't load run detail.";
}
